import React, { useState, useEffect, useRef } from "react";
import { FaBars, FaBell, FaSignOutAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import "./Header.css";

const EmployeeHeader = ({ setSidebarOpen }) => {
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const fetchEmployee = async () => {
      const token = localStorage.getItem("employeeToken");
      if (!token) {
        navigate("/login");
        return;
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/employee/me`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch employee data");
        }

        const data = await response.json();
        setEmployee(data.employee);
      } catch (error) {
        console.error(error);
        navigate("/login");
      } finally {
        setLoading(false);
      }
    };

    fetchEmployee();
  }, [navigate]);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/chat/admin-notifications`);
        if (!response.ok) throw new Error("Failed to fetch notifications.");
        const data = await response.json();
        setNotifications(data);
      } catch (error) {
        console.error("Error fetching notifications:", error.message);
      }
    };

    fetchNotifications();
    const interval = setInterval(fetchNotifications, 5000); // Fetch notifications every 5 seconds
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("employeeToken");
    navigate("/login");
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && 
          !event.target.closest('.notification-btn')) {
        setShowDropdown(false);
      }
    };

    // Add event listener when dropdown is shown
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    // Clean up event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const markNotificationsAsRead = async () => {
    try {
      const token = localStorage.getItem("employeeToken");
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/chat/mark-as-read`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ userType: 'employee' }),
      });
  
      if (!response.ok) {
        throw new Error("Failed to mark notifications as read.");
      }
  
      setNotifications([]);
      setShowDropdown(false);
    } catch (error) {
      console.error("Error marking notifications as read:", error.message);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <header className="dashboard-header">
      <button className="menu-btn" onClick={() => setSidebarOpen(true)}>
        <FaBars />
      </button>
      <h5>Welcome, {employee?.name} (Employee)</h5>

      <div className="header-right">
        <div className="notification-container">
          <button className="notification-btn" onClick={() => setShowDropdown(!showDropdown)}>
            <FaBell />
            {notifications.length > 0 && <span className="notification-badge">{notifications.length}</span>}
          </button>

          {showDropdown && (
            <div className="notification-dropdown" ref={dropdownRef}>
              {notifications.length > 0 ? (
                notifications.map((notif, index) => (
                  <div key={index} className="notification-item">
                    {notif.message}
                  </div>
                ))
              ) : (
                <div className="notification-item">No new notifications</div>
              )}
              <button onClick={markNotificationsAsRead} className="mark-as-read-btn">
                Mark as Read
              </button>
            </div>
          )}
        </div>

        <button className="logout-btn" onClick={handleLogout}>
          <FaSignOutAlt /> Logout
        </button>
      </div>
    </header>
  );
};

export default EmployeeHeader;